import { IContextualMenuItem } from '@fluentui/react';
import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import ViewContext from '../components/Context/ViewContext';
import { useDispatch, useSelector } from 'react-redux';
import { useListQuery } from '../graphql/containers/query';
import { DocumentNode } from 'graphql';
import { useLazyQuery } from '@apollo/client';
import dynamicReportQuery from '../graphql/queries/DynamicReport.graphql';
import { useBackgroundProcess } from '../utils/BackgroundJobs';
import canonicalNames from '../graphql/schema/canonicalNames.json';
import normalizeVariables from '../graphql/schema/normalizeVariables';
import Dropdown from '../components/connectedForm/Dropdown';
import * as input from '../components/connectedForm/inputComponents';
import { formatDateTime } from '../utils/DateHelper';
import CheckboxInput from '../components/connectedForm/CheckboxInput';
import getValue from '../components/connectedForm/utils/getValue';
import EmailEditor from '../components/report/EmailEditor';
import { BasicHeader } from './BasicHeader';
import fieldMetaData, { IEnumMeta } from '../graphql/schema/fieldMetaData';
import { ModalBottomButtonContainer } from '../components/modals/Styles';
import { SystemPrinter } from '../graphql/entities';
import { serviceActions } from '../state/modules/service';
import { default as Form } from '../components/connectedForm';
import { PrimaryButton, SecondaryButton } from '../components/connectedForm/Button';
import DynamicReportContext from '../components/Context/DynamicReportContext';
import UserContext from '../components/Context/UserContext';
import {
  AlertType,
  Direction,
  IColumnViewInput,
  ICompanyRoleAction,
  IDynamicReportQueryResult,
  IDynamicReportQueryVariables,
  IReportParametersInput,
  Maybe,
  ReportOutputType,
} from '../graphql/schema/p4types';
import WorkspaceContext from '../components/Context/WorkspaceContext';
import { cloneDeep } from '@apollo/client/utilities';
import FluentModal from '../components/modals/FluentModal';

export type ILabelValuePair = { label: string; value: string };

export type IDynamicReportEmailConfig = {
  emailTo: Array<ILabelValuePair>;
  emailCc: Array<ILabelValuePair>;
  emailFrom?: string;
  emailSubject?: string;
};

type IDynamicReportModalProps = {
  title: string;
  view: any;
  onClose: () => void;
  entityType: string;
  //canonicalName is optional if entityType is provided as the modal can determine this from canonicalNames.json
  canonicalName?: string;
  options: IEnumMeta<ReportOutputType>[];
};

export const DynamicReportModal = (props: IDynamicReportModalProps) => {
  const { canonicalName, options } = props;

  const [outputType, setOutputType] = useState();
  const [printerId, setPrinterId] = useState(null);

  const [emailConfig, setEmailConfig] = useState<IDynamicReportEmailConfig>({
    emailTo: [{ label: '', value: '' }],
    emailCc: [{ label: '', value: '' }],
  });

  const [reportChanges, setReportChanges] = useState(false);

  const systemPrinters = useListQuery(SystemPrinter.dropdown?.query as DocumentNode);

  const actionEntities = useSelector((store: any) => store.app.actionEntities);

  const dispatch = useDispatch();

  const { workspaceFilters, workspaceSelection } = useContext(WorkspaceContext);
  const {
    reportQuery,
    getQueryInput: getQueryInputFromContext,
    canonicalEntityName,
  } = useContext(DynamicReportContext);

  const [callDynamicReport, { data: dynamicReportJob }] = useLazyQuery<
    IDynamicReportQueryResult,
    IDynamicReportQueryVariables
  >(reportQuery || dynamicReportQuery, { fetchPolicy: 'network-only' });
  const runBackgroundProcess = useBackgroundProcess({
    title: 'Preparing dynamic report',
    onComplete: (job) => {
      const title = `${props.title} sent`;
      if (outputType?.value === ReportOutputType.SystemPrinter) {
        dispatch(serviceActions.genericAlert(title, `${props.title} will be sent to printer`, AlertType.Success));
      } else if (outputType?.value === ReportOutputType.Email) {
        dispatch(serviceActions.genericAlert(title, `${props.title} will be sent via email`, AlertType.Success));
      } else {
        const reportWindow = openFile(job.properties.reportFile);
        if (reportWindow && outputType?.value === ReportOutputType.LocalPrinter) {
          reportWindow.print();
        }
      }
    },
  });

  const user = useContext(UserContext);
  const emailAddress: Maybe<string> = user.currentUser.systemUser.email;

  useEffect(() => {
    if (emailAddress) {
      setEmailConfig((prevEmailConfig: IDynamicReportEmailConfig) => ({
        ...prevEmailConfig,
        emailTo: [{ label: emailAddress, value: emailAddress }],
      }));
    }
  }, [emailAddress]);

  useEffect(() => {
    if (dynamicReportJob) {
      const key = Object.keys(dynamicReportJob)[0];
      runBackgroundProcess(cloneDeep(dynamicReportJob[key]));

      props.onClose();
    }
  }, [dynamicReportJob]);

  useEffect(() => {
    if (options?.length > 0 && !outputType) {
      if (options?.some((value) => value.value === ReportOutputType.Pdf)) {
        setOutputType({
          label: 'PDF Document',
          value: ReportOutputType.Pdf,
        });
      } else {
        setOutputType({
          label: options[0]?.label,
          value: options[0]?.value,
        });
      }
    }
  }, [options?.length]);

  const getQueryInput = (
    columns: Array<Maybe<IColumnViewInput>>,
    view: any,
    reportParameters: Maybe<IReportParametersInput>
  ): IDynamicReportQueryVariables => {
    const entities = actionEntities[props.view.screen] ?? [];
    const entityIds = entities.map((value: { id: any }) => value.id);

    return {
      orderBy: props.view?.orderBy?.[0]?.fieldName || 'id',
      orderDirection: props.view?.orderBy?.[0]?.isDescending ? Direction.Desc : Direction.Asc,
      columns: columns,
      view: { id: view.id, name: view.name }, //This is all that's actually needed in the services...
      reportParameters: reportParameters,
      filters: workspaceFilters,
      canonicalEntityName: canonicalEntityName ?? canonicalNames[props.entityType] ?? canonicalName,
      include: !workspaceSelection.selectedAll,
      entityIds: entityIds,
      entities: entities,
      reportSinceLastUpdated: reportChanges,
    };
  };

  const print = () => {
    const title = `${props.title} could not be output`;

    if (!outputType?.value) {
      dispatch(serviceActions.genericAlert(title, 'Please select an output type', AlertType.Warning));
      return;
    }

    if (outputType?.value === ReportOutputType.SystemPrinter && printerId === null) {
      dispatch(serviceActions.genericAlert(title, 'Please select a system printer', AlertType.Warning));
      return;
    }

    let reportParameters: IReportParametersInput = {};

    if (outputType?.value === ReportOutputType.Email) {
      const { emailTo = [], emailCc = [], ...rest } = emailConfig;

      const emailToList = emailTo.map((email) => email.value).toString();
      const emailCcList = emailCc.map((email) => email.value).toString();

      reportParameters = { emailFrom: 'root@ogl.co.uk', emailToList, emailCcList, ...rest };
    }

    reportParameters.outputType = outputType?.value;
    reportParameters.printerId = printerId;

    const columns: any = [];

    let i = 1;
    props.view.columns.forEach((column) => {
      columns.push({
        position: i++,
        fieldName: column.fieldName,
        name: column.title,
        total: column.total,
        width: column.width ? column.width | 0 : 100, //Width needs to be coerced to an int.
      });
    });

    const view = normalizeVariables(props.view, [
      '__typename',
      'editing',
      'selected',
      'entityMetaData',
      'lastReported',
      'formatter',
      'options',
      'key',
      'cellClass',
      'sortable',
      'resizable',
      'currentViewName',
      'tabIndex',
      'testid',
      'label',
      'type',
      'showType',
    ]);
    if (view.filterObjects) {
      view.filterObjects = view.filterObjects.map((filterObj) => {
        return normalizeVariables(filterObj, ['query', 'valueKey', 'labelKey', 'objects']);
      });
    }
    if (view.filterAttributeObjects) {
      view.filterAttributeObjects = view.filterAttributeObjects.map((filterObj) => {
        return normalizeVariables(filterObj, ['query', 'valueKey', 'labelKey', 'objects']);
      });
    }
    if (view.filterEnums) {
      view.filterEnums = view.filterEnums.map((filterEn) => {
        return normalizeVariables(filterEn, ['objectType']);
      });
    }

    const defaultQueryInput = getQueryInput(columns, view, reportParameters);
    const dynamicReportInput: IDynamicReportQueryVariables =
      getQueryInputFromContext?.(defaultQueryInput) ?? defaultQueryInput;

    callDynamicReport({ variables: dynamicReportInput });
  };

  const onEmailUpdate = (emailConfig) => {
    setEmailConfig(emailConfig);
  };

  const openFile = (data) => {
    return window.open(`/api/resources/report/${data}`, '_blank');
  };

  let systemPrinterLookup: ReactElement | null = null;
  if (outputType?.value === ReportOutputType.SystemPrinter) {
    systemPrinterLookup = (
      <Form.Fieldset cols={1}>
        <Dropdown
          dataid="printerId"
          options={systemPrinters}
          component={input.Dropdown}
          label="System Printer"
          onChange={setPrinterId}
          value={printerId}
          valueKey="id"
          labelKey="printerName"
        />
      </Form.Fieldset>
    );
  }

  let reportChangesComponent: ReactElement | null = null;
  if (props.view?.name !== 'default') {
    let label;
    if (props.view.lastReported) {
      const lastReportedDate = new Date(props.view.lastReported);
      label = `Report Updated Since ${formatDateTime(lastReportedDate)}`;
    } else {
      label = 'Report Updated';
    }
    reportChangesComponent = (
      <CheckboxInput
        labelCols={6 / 12}
        inputCols={6 / 12}
        label={label}
        value={reportChanges}
        onChange={(value) => setReportChanges(getValue(value))}
        data-testid={'report-changes-component'}
      />
    );
  }

  const emailEditor =
    outputType?.value === ReportOutputType.Email ? (
      <EmailEditor {...emailConfig} allowEmailTo={true} onUpdate={onEmailUpdate} />
    ) : null;

  const actionName = 'Print';

  return (
    <>
      <BasicHeader>{props.title}</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Form.Fieldset cols={1}>
          {reportChangesComponent}
          <Dropdown
            dataid="outputType"
            options={options?.filter(
              (reportOutputType) => reportOutputType.value !== ReportOutputType.EmailWithPreview
            )}
            label="Output"
            onChange={setOutputType}
            value={outputType}
            valueKey="value"
            labelKey="label"
            clearable={false}
          />
        </Form.Fieldset>
        {systemPrinterLookup}
        {emailEditor}

        <ModalBottomButtonContainer>
          <SecondaryButton
            onClick={(e) => {
              e.preventDefault();
              props.onClose();
            }}
          >
            Skip
          </SecondaryButton>
          <PrimaryButton
            onClick={(e) => {
              e.preventDefault();
              print();
            }}
          >
            {actionName}
          </PrimaryButton>
        </ModalBottomButtonContainer>
      </div>
    </>
  );
};

export const useDynamicReportOptions = () => {
  const user = useContext(UserContext);
  const options = useMemo(() => {
    const isAdmin = user?.currentUser?.systemUser?.admin;
    let filteredOptions = fieldMetaData.ReportOutputType.enumValues;

    if (!isAdmin) {
      filteredOptions = filteredOptions.filter((option) => {
        return user?.currentUser?.companyRoleActions.some((action: ICompanyRoleAction) => {
          return action?.action && action.permitted && action.action.name === `DYNAMIC_${option.value}`;
        });
      });
    }

    return filteredOptions;
  }, [user?.currentUser?.systemUser?.admin, user?.currentUser?.companyRoleActions]);

  return {
    options,
    tooltip:
      options?.length === 0
        ? 'Dynamic reporting is not permitted - Dynamic reporting permissions can be configured under Company > User Roles by your system administrator'
        : null,
  };
};

export const useWorkspaceGenericReportAction: () => [IContextualMenuItem, ReactElement] = () => {
  const { selectionState, title, entity } = useContext(WorkspaceContext);
  const { views, viewScreenName } = useContext(ViewContext);

  const [dynamicReportModalOpen, setDynamicReportModalOpen] = useState(false);

  const { options, tooltip } = useDynamicReportOptions();

  const dynamicReportAction: IContextualMenuItem = {
    key: 'dynamicReport',
    text: 'Dynamic Report',
    onClick: () => {
      setDynamicReportModalOpen(true);
    },
    disabled: !views?.[viewScreenName]?.currentView || options?.length === 0,
    tooltip: tooltip,
  };

  return [
    dynamicReportAction,
    <FluentModal isOpen={dynamicReportModalOpen} styles={{ main: { minWidth: '500px', paddingBottom: '60px' } }}>
      <DynamicReportModal
        title={`${title} Report (${selectionState.count} Records)`}
        view={views?.[viewScreenName]?.currentView}
        entityType={entity?.type}
        onClose={() => setDynamicReportModalOpen(false)}
        options={options}
      />
    </FluentModal>,
  ];
};

export default useWorkspaceGenericReportAction;
